import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

interface QRScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (result: string) => void;
  title?: string;
  description?: string;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  isOpen,
  onClose,
  onScan,
  title = 'Scan QR Code',
  description = 'Position the QR code within the frame'
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (isOpen && !scannerRef.current) {
      startScanning();
    } else if (!isOpen && scannerRef.current) {
      stopScanning();
    }

    return () => {
      if (scannerRef.current) {
        stopScanning();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      const qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          // Successfully scanned
          handleScanSuccess(result.data);
        },
        {
          preferredCamera: 'environment', // Use back camera
          maxScansPerSecond: 10,
        }
      );

      await qrScanner.start();
      scannerRef.current = qrScanner;
    } catch (err: any) {
      setError(err.message || 'Failed to start camera');
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.destroy();
      } catch (err) {
      }
      scannerRef.current = null;
      setIsScanning(false);
    }
  };

  const handleScanSuccess = (decodedText: string) => {
    // Extract Stellar address if it's in a URL or other format
    let address = decodedText.trim();
    
    // Check if it's a Stellar address (starts with G and is 56 chars)
    const stellarAddressRegex = /G[A-Z0-9]{55}/;
    const match = address.match(stellarAddressRegex);
    
    if (match) {
      address = match[0];
    } else if (address.startsWith('stellar:')) {
      // Handle stellar: protocol URLs
      address = address.replace('stellar:', '').split('?')[0];
    } else if (address.includes('stellar.org/account/')) {
      // Handle stellar.org account URLs
      const urlMatch = address.match(/account\/(G[A-Z0-9]{55})/);
      if (urlMatch) {
        address = urlMatch[1];
      }
    }

    // Validate it's a Stellar address
    if (address.length === 56 && address.startsWith('G')) {
      stopScanning();
      onScan(address);
      onClose();
    } else {
      setError('Invalid Stellar address. Please scan a valid QR code.');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          stopScanning();
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '500px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: 'bold' }}>
          {title}
        </h2>
        <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#666', textAlign: 'center' }}>
          {description}
        </p>

        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            marginBottom: '20px',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: '#000'
          }}
        >
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
            playsInline
          />
        </div>

        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '4px',
              marginBottom: '12px',
              width: '100%',
              textAlign: 'center',
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}

        {!isScanning && !error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              borderRadius: '4px',
              marginBottom: '12px',
              width: '100%',
              textAlign: 'center',
              fontSize: '14px'
            }}
          >
            Requesting camera permission...
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <button
            onClick={() => {
              stopScanning();
              onClose();
            }}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
